import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  fetchApiGet,
  fetchApiPut,
  fetchApiPost,
  fetchApiDelete,
  fetchApiPostBlob,
  MimeType,
} from "../utils";
import { BaseDbObject } from "@/lib/models/db";
import { CreateContractTemplateValidation, DocumentTemplateInstance, DocumentTemplate } from "../signingContracts";
import { DocumentSigningStatusOptions, DocumentSigningStatus, getSigningStatusLabel } from "../signingContracts/index";
export { DocumentSigningStatusOptions, DocumentSigningStatus, getSigningStatusLabel };

export enum AcceptedCfrFields {
  CUSTOMER_NAME = "customer_name",
  CUSTOMER_EMAIL = "customer_email",
  CUSTOMER_PHONE = "customer_phone",
  CUSTOMER_ADDRESS = "customer_address",
  CUSTOMER_GPS_COORDINATES = "customer_gps_coordinates",
  LHC_SIGNATURE = "lhc_signature",
  CONTRACT_NUMBER = "contract_number",
  CONTRACT_TYPE = "contract_type",
  CONTRACT_QUANTITY = "contract_quantity",
  CONTRACT_SHIPPING_DATES = "contract_shipping_dates",
  CONTRACT_COMMODITY = "contract_commodity",
  CONTRACT_DATE = "contract_date",
  CONTRACT_CROP_YEAR = "contract_crop_year",

  CUSTOMER_DATE = "customer_date",
  CUSTOMER_SIGNATURE = "customer_signature",
}

const accepted_fields = Object.values(AcceptedCfrFields);

export const acceptedFieldsOptions = [
  { value: AcceptedCfrFields.CUSTOMER_NAME, label: "Customer Name" },
  { value: AcceptedCfrFields.CUSTOMER_ADDRESS, label: "Customer Address" },
  { value: AcceptedCfrFields.CUSTOMER_EMAIL, label: "Customer Email" },
  { value: AcceptedCfrFields.CUSTOMER_PHONE, label: "Customer Phone" },
  { value: AcceptedCfrFields.CUSTOMER_GPS_COORDINATES, label: "Customer GPS Coordinates" },
  { value: AcceptedCfrFields.CONTRACT_COMMODITY, label: "Commodity" },
  { value: AcceptedCfrFields.CONTRACT_NUMBER, label: "Contract Number" },
  { value: AcceptedCfrFields.CONTRACT_QUANTITY, label: "Contract Quantity" },
  { value: AcceptedCfrFields.CONTRACT_TYPE, label: "Contract Type" },
  { value: AcceptedCfrFields.CONTRACT_DATE, label: "Contract Date" },
  { value: AcceptedCfrFields.CONTRACT_SHIPPING_DATES, label: "Contract Shipping Dates" },
  { value: AcceptedCfrFields.CONTRACT_CROP_YEAR, label: "Contract Crop Year" },
  { value: AcceptedCfrFields.LHC_SIGNATURE, label: "LHC Signature" },

  { value: AcceptedCfrFields.CUSTOMER_DATE, label: "Customer Date" },
  { value: AcceptedCfrFields.CUSTOMER_SIGNATURE, label: "Customer Signature" },
];

export const CfrOptionalFieldInfo = z.object({
  contract_number: z.string().optional(),
  contract_type: z.string().optional(),
  contract_quantity: z.string().optional(),
  contract_shipping_dates: z.string().optional(),
  contract_date: z.string().optional(),
});

export const CreateTemplateInstanceValidation = z.object({
  cfr_document_id: z.string().uuid().optional(),
  group_id: z.string().uuid(),

  template_id: z.string().uuid(),
  customer_id: z.string().uuid(),

  document_title: z.string().optional(),

  is_test_send: z.boolean().default(false),
  is_draft: z.boolean().default(false),

  meta_fields: CfrOptionalFieldInfo.optional(),
  suggestion_id: z.string().uuid().optional(),
});

export const CreateTemplateValidation = z.object({
  commodity: z.string(),
  crop_year: z.number(),
  destination_id: z.string().uuid().optional(),
  customer_destination_id: z.string().uuid().optional(),

  template_content_type: z.enum(["cfr"]),
  parent_group_id: z.string().uuid().optional(),

  template: CreateContractTemplateValidation
}).refine((data) => {
  const fields = data.template.fields || [];
  const fieldNames = fields.map(field => field.field_id);
  const uniqueFieldNames = new Set(fieldNames);

  return fieldNames.length === uniqueFieldNames.size;
}, {
  message: "May not duplicate fields",
  path: ["template", "fields"]
});

export const BulkSendInstancesValidation = z.object({
  to_send: z.array(CreateTemplateInstanceValidation)
});

export const GenerateBulkPresignDownloadLinksValidation = z.object({
  instance_ids: z.array(z.string().uuid()),
});

export const DuplicateTemplateValidation = z.object({
  template_id: z.string().uuid(),
  crop_year: z.number().optional(),
  customer_destination_id: z.string().uuid().optional(),
  commodity: z.string().optional(),
  title: z.string().optional(),
});

export interface CfrDocument extends BaseDbObject {
  commodity_type: string;
  crop_year: number;
  destination_id?: string | null;
  customer_destination_id?: string | null;

  MarketZone?: { id: string; dtn_market_zone: string; };
  CfrInstances?: CfrDocumentInstance[];
  Destination?: { id: string; long_name: string };
  DocumentTemplate?: DocumentTemplate;
}

export interface CfrContract extends BaseDbObject {
  customer_id: string;
  cfr_document_id: string;

  CfrDocument?: CfrDocument;
  Customer?: { id: string; long_name: string };
  DocumentTemplateInstance?: DocumentTemplateInstance;
}

export interface CfrDocumentInstance extends BaseDbObject {
  customer_id: string;
  group_id: string;

  cfr_document_id: string;
  document_template_instance_id: string;

  Customer?: { id: string; long_name: string };
  CfrDocument?: CfrDocument;
  DocumentTemplateInstance?: DocumentTemplateInstance;
}

export interface DocumentSuggestion extends BaseDbObject {
  feature_type: string;
  customer_id?: string;
  market_zone_id?: string;
  destination_id?: string;

  commodity?: string;
  crop_year?: number;
  contract_number?: string;
  contract_quantity?: string;

  original_meta?: any;

  Customer?: { id: string; long_name: string }
  MarketZone?: { id: string; dtn_market_zone: string; market_zone: string }
  Destination?: { id: string; long_name: string }
}

export interface CfrSuggestion extends DocumentSuggestion {
  feature_type: "cfr";
  suggestion_type: "updated_report" | "customer_mark" | "commodity_mark";

  original_meta?: { client_id?: string; customer_name?: string; market_zone?: string; contract_type?: string; shipment_range?: string; };
}

export type ListCfrTemplateItem = Pick<CfrDocument, "id" | "commodity_type" | "crop_year" | "destination_id" | "DocumentTemplate" | "customer_destination_id">;
export type ListCfrContractItem = Pick<CfrContract, "customer_id" | "id" | "created_at" | "Customer" | "CfrDocument" | "DocumentTemplateInstance" | "cfr_document_id">

export interface CfrListTemplateResponse {
  data: ListCfrTemplateItem[]
}

export interface CfrListSuggestionsResponse {
  data: CfrSuggestion[]
}

export interface CfrListContractResponse {
  data: ListCfrContractItem[]
}

export interface PurchaseReleaseBreakdownItem {
  // CFR Document Template ID
  id: string;
  
  cfr_document_id?: string;
  document_template_id?: string;

  // Customer ID from the purchase release suggestion
  customer_id: string | null;
  
  // Commodity type (e.g., "corn", "soybeans", "wheat")
  commodity_type: string;
  
  // Crop year (e.g., 2024, 2025)
  crop_year: number;

  customer: { id: string; long_name: string | null } | null;
  // Destination customer information
  destination: {
    id: string;
    long_name: string | null;
  } | null;
  
  // Document creation timestamp
  created_at: Date; // ISO 8601 string in JSON
  
  // Document last update timestamp
  updated_at: Date; // ISO 8601 string in JSON
  
  // Optional: CFR Document Instance ID (present if document has been sent)
  instance_id?: string;
  
  // Optional: Document Signing Session ID (present if document has a signing session)
  signing_session_id?: string;
  
  // Optional: Timestamp when document was completed/signed (present if signed)
  completed_at?: Date | null; // ISO 8601 string in JSON, null if not signed
  
  // Optional: Last status of the signing session
  // Possible values: "uploading", "sent", "archived", "deleted", "trashed", 
  //                  "cancelled", "revoked", "declined", "completed", "expired"
  last_status?: string;
  
  // Optional: Timestamp when document was cancelled (present if cancelled)
  cancelled_at?: Date | null; // ISO 8601 string in JSON
  
  // Optional: Timestamp when document was revoked (present if revoked)
  revoked_at?: Date | null; // ISO 8601 string in JSON
  
  // Optional: Timestamp when document was declined (present if declined)
  declined_at?: Date | null; // ISO 8601 string in JSON
  
  // Optional: Timestamp when document expired (present if expired)
  expired_at?: Date | null; // ISO 8601 string in JSON
}

export interface PurchaseReleaseBreakdownResponse {
  notSent: PurchaseReleaseBreakdownItem[];
  sent: PurchaseReleaseBreakdownItem[];
  signed: PurchaseReleaseBreakdownItem[];
  other: PurchaseReleaseBreakdownItem[];
}

export function useCreateCfrTemplate() {
  const queryClient = useQueryClient();

  return async (params: typeof CreateTemplateValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<{ upload_url: string; cfr_document_id: string; }>(
      `contracts/cfr/template`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["cfr"] });
    }

    return result;
  };
}

export function useDuplicateTemplate() {
  const queryClient = useQueryClient();

  return async (params: typeof DuplicateTemplateValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<{ cfr_document_id: string; }>(
      `contracts/cfr/template/${params.template_id}/duplicate`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["cfr"] });
    }

    return result;
  };
}



export function useCreateCfrTemplateInstance() {
  const queryClient = useQueryClient();

  return async (params: typeof CreateTemplateInstanceValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<{ batch_id: string; status: string; customer_group_id?: string }>(
      `contracts/cfr/template/instance`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["cfr"] });
      queryClient.invalidateQueries({ queryKey: ["cfr", "bulk-send-batches"] });
    }

    return result;
  };
}

export function useGetCfrTemplateDocument() {
  const queryClient = useQueryClient();

  return async (template_id: string) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<{ document_url: string; }>(
      `contracts/cfr/template/${template_id}/file`,
      {},
      queryParams
    );

    return result;
  };
}

export function useGetCfrPreviewTemplateDocument() {
  const queryClient = useQueryClient();

  return async (params: typeof CreateTemplateInstanceValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPostBlob(
      `contracts/cfr/template/${params.template_id}/preview`,
      params,
      MimeType.Json,
      queryParams
    );

    return result;
  };
}

export function useGetCfrInstanceDocument() {
  const queryClient = useQueryClient();

  return async (template_id: string, instance_id: string) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<{ sent_document_url?: string; final_document_url: string; }>(
      `contracts/cfr/template/${template_id}/instance/${instance_id}/file`,
      {},
      queryParams
    );

    return result;
  };
}

export function useListCfrSuggestions() {
  return useQuery({
    queryKey: ["cfr", "suggestions"],
    queryFn: () => fetchApiGet<CfrListSuggestionsResponse>(`contracts/cfr/suggestions`),
    retry: 1,
  });
}

export function useListCfrPurchaseReleaseBreakdown() {
  return useQuery({
    queryKey: ["cfr", "reports", "purchase-release"],
    queryFn: () => fetchApiGet<PurchaseReleaseBreakdownResponse>(`contracts/cfr/suggestions/purchase_release`),
    retry: 1,
  });
}


export function useListCfrTemplates() {
  return useQuery({
    queryKey: ["cfr", "templates"],
    queryFn: () => fetchApiGet<CfrListTemplateResponse>(`contracts/cfr/template`),
    retry: 1,
  });
}

export function useListCfrContracts({ crop_year }: { crop_year?: number } = {}) {
  const queryParams = new URLSearchParams();
  if (crop_year !== undefined) {
    queryParams.set("crop_year", String(crop_year));
  }
  return useQuery({
    queryKey: ["cfr", "contracts", { crop_year: crop_year ?? null }],
    queryFn: () =>
      fetchApiGet<CfrListContractResponse>(`contracts/cfr/contracts`, queryParams),
    retry: 1,
  });
}

export function useGetCfrTemplate({ id, enabled }: { id: string; enabled?: boolean }) {
  return useQuery({
    queryKey: ["cfr", "template", id],
    queryFn: () => fetchApiGet<CfrDocument>(`contracts/cfr/template/${id}`),
    retry: 1,
    enabled
  });
}

export function useGetCfrInstance({ instance_id, template_id, enabled }: { template_id: string; instance_id: string; enabled?: boolean }) {
  return useQuery({
    queryKey: ["cfr", "template", template_id, "instance", instance_id],
    queryFn: () => fetchApiGet<CfrDocumentInstance>(`contracts/cfr/template/${template_id}/instance/${instance_id}`),
    retry: 1,
    enabled
  });
}

export const UploadReportValidation = z.object({
  filename: z.string(),
  mime_type: z.enum([MimeType.Xlsx, MimeType.Csv]),
  crop_year: z.number().optional(),
  report_type: z.enum(["direct_ship", "purchase_release"]).default("direct_ship")
}).refine((data) => {
  if (data.report_type === "purchase_release") {
    return data.crop_year !== undefined;
  }
  return true;
}, {
  message: "crop_year is required when report_type is purchase_release",
  path: ["crop_year"]
}).refine((data) => {
  if (data.report_type === "direct_ship") {
    return data.mime_type === MimeType.Xlsx;
  }
  return true;
}, {
  message: "Expected excel file when report_type is direct_ship",
  path: ["mime_type"]
}).refine((data) => {
  if (data.report_type === "purchase_release") {
    return data.mime_type === MimeType.Csv;
  }
  return true;
}, {
  message: "Expected csv file when report_type is purchase_release",
  path: ["mime_type"]
});



export interface CfrSuggestionsUploadReportResult {
  link: string;
  document_id: string;
  job_id: string;
}

export function useGenerateCfrSuggestionsReportUpload() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof UploadReportValidation>) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<CfrSuggestionsUploadReportResult>(
      `contracts/cfr/report/upload`,
      data,
    );

    return result;
  };
}

/**
 * @deprecated
 *
 * The legacy `/contracts/cfr/template/instance/bulk` route is now
 * deprecated server-side and returns a ValidationError directing
 * callers at the staged-batch routes. Use the
 * `useCreateCfrBulkSendBatch` / `useAppendCfrBulkSendBatchItems` /
 * `useFinalizeCfrBulkSendBatch` hooks below instead.
 */
export function useCreateBulkInstances() {
  return async (data: z.infer<typeof BulkSendInstancesValidation>) => {
    const result = await fetchApiPost<{ job_ids: string[] }>(
      `contracts/cfr/template/instance/bulk`,
      data,
    );

    return result;
  };
}

export function useGenerateBulkPresignDownloadLinks() {
  return async (data: z.infer<typeof GenerateBulkPresignDownloadLinksValidation>) => {
    // Mono-bun response shape: { presigned_urls, document_names }.
    // Length-N: one URL per UNIQUE signing session in the selected
    // instance set (bulk-envelope dedupe — spec §15.2). For legacy
    // 1:1 rows there is still one URL per selected instance.
    const result = await fetchApiPost<{ presigned_urls: string[]; document_names: string[] }>(
      `contracts/cfr/template/file/download`,
      data,
    );

    return result;
  };
}

// ---------- CFR bulk-send staged-batch hooks ----------
//
// Spec §2 + §7. The frontend creates a batch, appends the would-be
// instance payloads in chunks, then finalizes. The server groups items
// by customer at finalize time and enqueues one envelope job per valid
// customer group. Per-customer-group validation/job status is exposed
// through `useGetCfrBulkSendBatch`.

export const CreateCfrBulkSendBatchValidation = z.object({
  expected_item_count: z.number().int().nonnegative().optional(),
  meta_data: z.record(z.string(), z.unknown()).optional(),
});

export const AppendCfrBulkSendBatchItemValidation = z.object({
  client_item_id: z.string().min(1),
  payload: CreateTemplateInstanceValidation,
});

export const AppendCfrBulkSendBatchItemsValidation = z.object({
  items: z.array(AppendCfrBulkSendBatchItemValidation).min(1),
});

export type CreateCfrBulkSendBatchInput = z.infer<typeof CreateCfrBulkSendBatchValidation>;
export type AppendCfrBulkSendBatchItemsInput = z.infer<typeof AppendCfrBulkSendBatchItemsValidation>;

export interface CfrBulkSendBatchItemView {
  id: string;
  client_item_id: string;
  customer_id: string | null;
  cfr_document_id: string | null;
  document_template_id: string | null;
  suggestion_id: string | null;
  customer_group_id: string | null;
  status: string;
  validation_error: string | null;
  cfr_document_instance_id: string | null;
  document_template_instance_id: string | null;
  created_at: string;
}

export interface CfrBulkSendCustomerGroupView {
  id: string;
  customer_id: string;
  envelope_id: string;
  status: string;
  item_count: number;
  job_id: string | null;
  signing_session_id: string | null;
  envelope_document_title: string | null;
  error_message: string | null;
  enqueued_at: string | null;
  sent_at: string | null;
  halted_at: string | null;
  customer_long_name?: string | null;
  customer: { id: string; long_name: string } | null;
  /** Latest signing-session status, joined when signing_session_id is present. */
  signing_session_last_status: string | null;
  items: CfrBulkSendBatchItemView[];
}

export interface CfrBulkSendBatchView {
  id: string;
  status: string;
  expected_item_count: number | null;
  uploaded_item_count: number;
  created_by_id: string;
  created_at: string;
  updated_at: string;
  finalized_at: string | null;
  cancelled_at: string | null;
  customer_groups: CfrBulkSendCustomerGroupView[];
  /** Items not yet assigned to a customer group (pre-finalize). */
  ungrouped_items: CfrBulkSendBatchItemView[];
}

export function useCreateCfrBulkSendBatch() {
  return async (data: z.infer<typeof CreateCfrBulkSendBatchValidation>) => {
    return await fetchApiPost<{ batch_id: string }>(
      `contracts/cfr/bulk-send-batches`,
      data,
    );
  };
}

export function useAppendCfrBulkSendBatchItems() {
  return async ({ batch_id, items }: { batch_id: string; items: AppendCfrBulkSendBatchItemsInput["items"] }) => {
    return await fetchApiPost<{ uploaded_count: number; uploaded_item_count: number }>(
      `contracts/cfr/bulk-send-batches/${batch_id}/items`,
      { items },
    );
  };
}

export function useFinalizeCfrBulkSendBatch() {
  const queryClient = useQueryClient();
  return async ({ batch_id }: { batch_id: string }) => {
    const result = await fetchApiPost<CfrBulkSendBatchView>(
      `contracts/cfr/bulk-send-batches/${batch_id}/finalize`,
      {},
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["cfr", "bulk-send-batches", batch_id] });
      queryClient.invalidateQueries({ queryKey: ["cfr"] });
    }
    return result;
  };
}

type CfrBulkSendBatchQueryData = Awaited<ReturnType<typeof fetchApiGet<CfrBulkSendBatchView>>>;

export function useGetCfrBulkSendBatch({
  batch_id,
  enabled,
  refetchInterval,
}: {
  batch_id: string;
  enabled?: boolean;
  /**
   * Static interval or a function. The function form receives the
   * latest data and should return `false` to stop polling. Matches
   * tanstack v4's signature.
   */
  refetchInterval?: number | false | ((data: CfrBulkSendBatchQueryData | undefined) => number | false);
}) {
  return useQuery({
    queryKey: ["cfr", "bulk-send-batches", batch_id],
    queryFn: () => fetchApiGet<CfrBulkSendBatchView>(`contracts/cfr/bulk-send-batches/${batch_id}`),
    retry: 1,
    enabled,
    refetchInterval,
  });
}

export const DownloadInstanceReportValidation = z.object({
  created_start_date: z.string().refine((val: string) => /^\d{4}-\d{2}-\d{2}$/.test(val), "Start date must be in YYYY-MM-DD format"),
  created_end_date: z.string().refine((val: string) => /^\d{4}-\d{2}-\d{2}$/.test(val), "End date must be in YYYY-MM-DD format"),
});

export interface DownloadInstanceReportResponse {
  document_id: string;
  presigned_get_url: string;
}

export function useDownloadInstanceReport() {
  return async (params: typeof DownloadInstanceReportValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<DownloadInstanceReportResponse>(
      `contracts/cfr/template/instance/download`,
      params,
      queryParams
    );

    return result;
  };
}
